import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { ABLE_COLORS } from '../constants';

/**
 * AnimatedCheckbox - A satisfying checkbox with completion animation
 *
 * Props:
 * - checked: boolean - current state
 * - onChange: function - callback when toggled
 * - size: number - size in pixels (default 22)
 * - disabled: boolean - disable interaction
 */
export default function AnimatedCheckbox({
  checked = false,
  onChange,
  size = 22,
  disabled = false
}) {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    if (disabled) return;

    if (!checked) {
      // Trigger animation when checking
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 400);
    }

    onChange?.(!checked);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: '6px',
        border: `2px solid ${checked ? ABLE_COLORS.semantic.success : ABLE_COLORS.border}`,
        background: checked ? ABLE_COLORS.semantic.success : 'white',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: 'all 0.2s ease',
        transform: isAnimating ? 'scale(1.15)' : 'scale(1)',
        boxShadow: isAnimating
          ? `0 0 0 4px ${ABLE_COLORS.semantic.success}25`
          : 'none',
        opacity: disabled ? 0.5 : 1
      }}
      onMouseEnter={(e) => {
        if (!disabled && !checked) {
          e.currentTarget.style.borderColor = ABLE_COLORS.semantic.success;
          e.currentTarget.style.background = `${ABLE_COLORS.semantic.success}10`;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !checked) {
          e.currentTarget.style.borderColor = ABLE_COLORS.border;
          e.currentTarget.style.background = 'white';
        }
      }}
      aria-checked={checked}
      role="checkbox"
    >
      {checked && (
        <Check
          size={size - 8}
          color="white"
          strokeWidth={3}
          style={{
            animation: isAnimating ? 'checkmark-pop 0.3s ease' : 'none'
          }}
        />
      )}
      <style>{`
        @keyframes checkmark-pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </button>
  );
}
